import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';

export interface CustomerListParams {
  q?: string;
  page?: number;
  pageSize?: number;
  includeArchived?: boolean;
  idsOnly?: boolean;
}

export interface CustomerListItem {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  loyaltyPointsBalance?: number;
  storeCreditBalance?: string;
  totalTickets: number;
  totalSpend: string;
  lastVisitAt?: string | null;
  customerStatus?: string;
  statusReason?: string | null;
  archivedAt?: string | null;
  archiveReason?: string | null;
  anonymizedAt?: string | null;
}

export type AcquisitionSource =
  | 'walk_in'
  | 'google_search'
  | 'social_media'
  | 'referral'
  | 'repeat_customer'
  | 'advertisement'
  | 'other';

export const ACQUISITION_SOURCE_OPTIONS: Array<{ value: AcquisitionSource; label: string }> = [
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'google_search', label: 'Google search' },
  { value: 'social_media', label: 'Social media' },
  { value: 'referral', label: 'Referral' },
  { value: 'repeat_customer', label: 'Repeat customer' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'other', label: 'Other' },
];

export type PreferredContact = 'sms' | 'email' | 'phone' | 'none';
export type UpdateFrequency = 'every_update' | 'status_change' | 'ready_only';

export interface CustomerDetail extends CustomerListItem {
  phone2?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  notes?: string | null;
  customerStatus?: string;
  statusReason?: string | null;
  statusNotes?: string | null;
  statusSetBy?: string | null;
  statusSetAt?: string | null;
  importedFrom?: string | null;
  createdAt?: string;
  preferredContact?: PreferredContact | null;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  quietHoursEnabled?: boolean;
  languagePreference?: string | null;
  marketingOptIn?: boolean;
  surveyOptIn?: boolean;
  reviewRequestOptIn?: boolean;
  updateFrequency?: UpdateFrequency | null;
  smsOptIn?: boolean;
  portalEnabled?: boolean;
  portalEmail?: string | null;
  portalLastLogin?: string | null;
  portalCanCreateTickets?: boolean;
  portalCreatedAt?: string | null;
  acquisitionSource?: AcquisitionSource | null;
  acquisitionSourceDetail?: string | null;
  referredByCustomerId?: string | null;
  referredByCustomerName?: string | null;
  archivedAt?: string | null;
  archivedBy?: string | null;
  archivedByName?: string | null;
  archiveReason?: string | null;
  anonymizedAt?: string | null;
  anonymizedBy?: string | null;
  referredCustomers?: Array<{ id: string; name: string; createdAt: string }>;
  sharedPhoneCustomers?: Array<{
    id: string;
    firstName: string;
    lastName: string | null;
    totalTickets: number;
    lastVisitAt: string | null;
  }>;
  recentTickets: Array<{
    id: string;
    ticketNumber: string;
    status: string;
    total: string;
    paidTotal?: string;
    importedFrom?: string | null;
    createdAt: string;
  }>;
  deviceHistory?: Array<{
    makeId?: string | null;
    modelId?: string | null;
    makeName?: string | null;
    modelName?: string | null;
    customModelName?: string | null;
    lastSeenAt: string;
    repairCount: number;
    conditionAtLastIntake?: string | null;
  }>;
}

export function listCustomers(params?: CustomerListParams) {
  return apiGet<CustomerListItem[], PaginatedMeta>('/api/v1/customers', { params });
}

export function getCustomer(id: string) {
  return apiGet<CustomerDetail>(`/api/v1/customers/${id}`).then((res) => res.data);
}

export function createCustomer(body: Record<string, unknown>) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>('/api/v1/customers', body).then(
    (res) => res.data,
  );
}

export function updateCustomer(id: string, body: Record<string, unknown>) {
  return apiPatch<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/customers/${id}`,
    body,
  ).then((res) => res.data);
}

export type CustomerDeviceRecentTicket = {
  id: string;
  ticketNumber: string;
  date: string;
  repairSummary: string;
};

export type CustomerDeviceHistoryItem = {
  makeId: string | null;
  makeName: string | null;
  modelId: string | null;
  modelName: string | null;
  customModelName: string | null;
  deviceCategory: string | null;
  lastImei: string | null;
  lastCondition: string | null;
  lastPasscodeType: string | null;
  lastColor: string | null;
  lastSerial: string | null;
  lastTicketId: string;
  lastTicketNumber: string;
  lastTicketDate: string;
  lastRepairSummary: string;
  totalRepairs: number;
  visitCount?: number;
  displayName: string;
  recentTickets?: CustomerDeviceRecentTicket[];
};

export function listCustomerDevices(customerId: string) {
  return apiGet<CustomerDeviceHistoryItem[]>(`/api/v1/customers/${customerId}/devices`).then(
    (res) => res.data,
  );
}

export function maskImei(imei: string | null | undefined): string {
  if (!imei || imei.length < 8) return imei ?? '';
  const first6 = imei.slice(0, 6);
  const last3 = imei.slice(-3);
  const masked = '•'.repeat(Math.max(0, imei.length - 9));
  return `${first6}${masked}${last3}`;
}

export type StoreCreditLedgerEntry = {
  id: string;
  amount: string;
  type: string;
  balanceAfter: string;
  reason: string | null;
  notes: string | null;
  ticketId: string | null;
  issuedBy: string | null;
  actorName: string | null;
  createdAt: string;
};

export type LoyaltyPointsLedgerEntry = {
  id: string;
  delta: number;
  balanceAfter: number;
  sourceType: string;
  notes: string | null;
  sourceId: string | null;
  issuedBy: string | null;
  actorName: string | null;
  createdAt: string;
};

export function getCustomerStoreCreditLedger(customerId: string, limit = 50) {
  return apiGet<StoreCreditLedgerEntry[]>(
    `/api/v1/customers/${customerId}/store-credit/ledger`,
    { params: { limit } },
  ).then((res) => res.data);
}

export function getCustomerLoyaltyPointsLedger(customerId: string, limit = 50) {
  return apiGet<LoyaltyPointsLedgerEntry[]>(
    `/api/v1/customers/${customerId}/loyalty-points/ledger`,
    { params: { limit } },
  ).then((res) => res.data);
}

export function adjustCustomerStoreCredit(
  customerId: string,
  body: { amount: number; reason: string; notes?: string | null },
) {
  return apiPost<Record<string, unknown>, typeof body>(
    `/api/v1/customers/${customerId}/store-credit`,
    body,
  ).then((res) => res.data);
}

export function adjustCustomerLoyaltyPoints(
  customerId: string,
  body: { delta: number; reason: string; notes?: string | null },
) {
  return apiPost<Record<string, unknown>, typeof body>(
    `/api/v1/customers/${customerId}/loyalty-points`,
    body,
  ).then((res) => res.data);
}

export type DuplicateStrategy =
  | 'exact_email'
  | 'phone_and_full_name'
  | 'phone_family'
  | 'phone_shared'
  | 'name_and_address'
  | 'fuzzy_name'
  | 'all';

export type DuplicateCustomerCard = {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  customerStatus: string;
  statusReason: string | null;
  createdAt: string;
  importedFrom: string | null;
  ticketCount: number;
  totalSpend: string;
  loyaltyPoints: number;
  storeCredit: string;
  lastVisitAt: string | null;
};

export type DuplicateGroup = {
  groupId: string;
  strategy: Exclude<DuplicateStrategy, 'all'>;
  confidence: 'high' | 'medium' | 'low';
  matchedOn: string;
  warningLabel?: string | null;
  customers: DuplicateCustomerCard[];
};

export function fetchCustomerDuplicates(params: {
  strategy?: DuplicateStrategy;
  minConfidence?: 'high' | 'medium' | 'low';
  page?: number;
  pageSize?: number;
}) {
  return apiGet<DuplicateGroup[]>('/api/v1/customers/duplicates', { params }).then((res) => ({
    data: res.data,
    meta: res.meta as PaginatedMeta | undefined,
  }));
}

export function lookupCustomersByPhone(phone: string, excludeId?: string) {
  return apiGet<
    Array<{
      id: string;
      firstName: string;
      lastName: string | null;
      email: string | null;
      phone: string | null;
      totalTickets: number;
      lastVisitAt: string | null;
      customerStatus: string;
    }>
  >('/api/v1/customers/by-phone', {
    params: { phone, ...(excludeId ? { excludeId } : {}) },
  }).then((res) => res.data);
}

export function mergeCustomers(body: {
  primaryId: string;
  duplicateIds: string[];
  fieldSelections: Record<string, string>;
  reason: string;
}) {
  return apiPost<{ mergeId: string; primaryId: string; absorbedIds: string[] }, typeof body>(
    '/api/v1/customers/merge',
    body,
  ).then((res) => res.data);
}

export function listCustomerMerges() {
  return apiGet<
    Array<{
      id: string;
      primaryCustomerId: string;
      absorbedCount: number;
      reason: string | null;
      mergedBy: string | null;
      mergedAt: string;
      reversedAt: string | null;
      reversedBy: string | null;
    }>
  >('/api/v1/customers/merges').then((res) => res.data);
}

export function undoCustomerMerge(mergeId: string) {
  return apiPost<{ primaryId: string; restoredIds: string[] }, Record<string, never>>(
    `/api/v1/customers/merges/${mergeId}/undo`,
    {},
  ).then((res) => res.data);
}

/** Repair denormalized customer totals for the whole tenant (tenant_admin). */
export function recalculateAllCustomerTotals() {
  return apiPost<{ recalculated: number }, Record<string, never>>(
    '/api/v1/customers/recalculate-totals',
    {},
  ).then((res) => res.data);
}


export type CustomerReferences = {
  total: number;
  byTable: Record<string, number>;
  hasOpenTickets: boolean;
  hasFinancialRecords: boolean;
  canHardDelete: boolean;
  openTickets: Array<{ id: string; ticketNumber: string; status: string }>;
};

export function getCustomerReferences(customerId: string) {
  return apiGet<CustomerReferences>(`/api/v1/customers/${customerId}/references`).then(
    (res) => res.data,
  );
}

export function archiveCustomer(customerId: string, body?: { reason?: string | null }) {
  return apiPost<{ id: string; archivedAt: string | null }, { reason?: string | null }>(
    `/api/v1/customers/${customerId}/archive`,
    body ?? {},
  ).then((res) => res.data);
}

export function unarchiveCustomer(customerId: string) {
  return apiPost<{ id: string; archivedAt: string | null }, Record<string, never>>(
    `/api/v1/customers/${customerId}/unarchive`,
    {},
  ).then((res) => res.data);
}

export function deleteCustomer(
  customerId: string,
  body: {
    reason: string;
    confirmName: string;
    managerOverrideAuthorizedBy: string;
  },
) {
  return apiDelete<{ deleted: boolean; id: string }>(`/api/v1/customers/${customerId}`, {
    data: body,
  }).then((res) => res.data);
}

export function anonymizeCustomer(
  customerId: string,
  body: {
    reason: string;
    confirmText: 'ANONYMIZE';
    managerOverrideAuthorizedBy: string;
  },
) {
  return apiPost<{ id: string; anonymizedAt: string | null }, typeof body>(
    `/api/v1/customers/${customerId}/anonymize`,
    body,
  ).then((res) => res.data);
}

export type BulkArchiveResult = {
  dryRun: boolean;
  archived: string[];
  skipped: Array<{
    id: string;
    name: string;
    reason: string;
    openTickets?: Array<{ id: string; ticketNumber: string; status: string }>;
  }>;
};

export function bulkArchiveCustomers(body: {
  customerIds: string[];
  reason?: string | null;
  dryRun?: boolean;
}) {
  return apiPost<BulkArchiveResult, typeof body>('/api/v1/customers/bulk/archive', body).then(
    (res) => res.data,
  );
}

export function bulkUnarchiveCustomers(body: {
  customerIds: string[];
  dryRun?: boolean;
}) {
  return apiPost<
    { dryRun: boolean; unarchived: string[]; skipped: Array<{ id: string; name: string; reason: string }> },
    typeof body
  >('/api/v1/customers/bulk/unarchive', body).then((res) => res.data);
}

export type BulkDeleteResult = {
  dryRun: boolean;
  deleted: string[];
  skipped: Array<{
    id: string;
    name: string;
    reason: string;
    byTable?: Record<string, number>;
  }>;
};

export function bulkDeleteCustomers(body: {
  customerIds: string[];
  reason: string;
  confirmText: 'DELETE';
  managerOverrideAuthorizedBy?: string;
  dryRun?: boolean;
}) {
  return apiPost<BulkDeleteResult, typeof body>('/api/v1/customers/bulk/delete', body).then(
    (res) => res.data,
  );
}

export type BulkMergeSurvivorStrategy = 'manual' | 'most_tickets' | 'oldest' | 'most_complete';
export type BulkMergeFieldStrategy = 'primary_wins' | 'fill_blanks';

export type BulkMergePreview = {
  primary: {
    id: string;
    name: string;
    tickets: number;
    spend: string;
    loyaltyPoints: number;
    storeCredit: string;
    email: string | null;
    phone: string | null;
    createdAt: string;
    customerStatus: string;
  };
  absorbing: number;
  totals: {
    ticketsAfter: number;
    spendAfter: string;
    loyaltyAfter: number;
    storeCreditAfter: string;
  };
  fieldResolution: Array<{ field: string; value: string | null; source: string }>;
  warnings: Array<{ level: 'high' | 'medium'; message: string; customerIds?: string[] }>;
  resolvedPrimaryId: string;
  openTicketBlockers: Array<{
    customerId: string;
    name: string;
    tickets: Array<{ id: string; ticketNumber: string; status: string }>;
  }>;
  customers: Array<{
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    tickets: number;
    spend: string;
    createdAt: string;
    importedFrom: string | null;
    customerStatus: string;
  }>;
};

export type BulkMergeBody = {
  customerIds: string[];
  primaryId: string;
  survivorStrategy: BulkMergeSurvivorStrategy;
  fieldStrategy: BulkMergeFieldStrategy;
  reason: string;
  managerOverrideAuthorizedBy?: string;
};

export function previewBulkCustomerMerge(
  body: Omit<BulkMergeBody, 'managerOverrideAuthorizedBy' | 'reason'> & { reason?: string },
) {
  return apiPost<BulkMergePreview, typeof body>('/api/v1/customers/bulk/merge/preview', body).then(
    (res) => res.data,
  );
}

export function bulkMergeCustomers(body: BulkMergeBody) {
  return apiPost<
    {
      mergeId: string;
      primaryId: string;
      absorbedIds: string[];
      absorbing: number;
      totals: BulkMergePreview['totals'];
    },
    BulkMergeBody
  >('/api/v1/customers/bulk/merge', body).then((res) => res.data);
}

export function listCustomerIds(params?: Omit<CustomerListParams, 'idsOnly' | 'page' | 'pageSize'>) {
  return apiGet<string[], PaginatedMeta>('/api/v1/customers', {
    params: { ...params, idsOnly: true, page: 1, pageSize: 5000 },
  });
}
